/**
 * Remembers what the user did with a reminder toast ACROSS page reloads.
 *
 * The toast components keep "already shown" in a ref, which dies with the
 * tab - so clicking "Remind in 5 min" and then reloading brought the card
 * straight back (the reminder time was still inside the catch-up window).
 * This module writes the decision to localStorage instead:
 *
 *   - Dismiss           -> that reminder never pops again
 *   - Remind in 5 min   -> it pops again at now+5min, reload or not
 *
 * Entries are keyed by "<kind>:<id>" and remember WHICH remind-at they
 * apply to: if the task/event is later rescheduled, the old decision no
 * longer matches and the new reminder pops normally. Stale entries are
 * pruned on every read. localStorage can be unavailable (private mode) -
 * every access is guarded, and without storage the app simply falls back
 * to the old within-this-tab behaviour.
 */

const STORAGE_KEY = 'reminder-toast-decisions';
/** Entries whose reminder is older than this are pruned. */
const KEEP_MS = 48 * 3_600_000;

interface Decision {
  /** The remind-at this decision was made for (ISO). */
  remindAt: string;
  /** Epoch ms to show again at; null = dismissed for good. */
  showAt: number | null;
}

type Store = Record<string, Decision>;

function read(): Store {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Store) : {};
  } catch {
    return {};
  }
}

function write(store: Store): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Storage full or blocked - degrade to in-memory behaviour silently.
  }
}

function prune(store: Store): Store {
  const cutoff = Date.now() - KEEP_MS;
  const kept: Store = {};
  for (const [key, decision] of Object.entries(store)) {
    const at = new Date(decision.remindAt).getTime();
    if (Number.isFinite(at) && at >= cutoff) kept[key] = decision;
  }
  return kept;
}

/**
 * What was decided about this reminder, if anything.
 *   'none'      - no decision recorded (or the reminder was rescheduled)
 *   'dismissed' - user dismissed it; never show again
 *   number      - user snoozed it; show again at this epoch ms
 */
export function getReminderDecision(
  kind: 'task' | 'event',
  id: string,
  remindAt: string,
): 'none' | 'dismissed' | number {
  const decision = read()[`${kind}:${id}`];
  if (!decision || decision.remindAt !== remindAt) return 'none';
  return decision.showAt === null ? 'dismissed' : decision.showAt;
}

export function rememberDismissed(kind: 'task' | 'event', id: string, remindAt: string): void {
  const store = prune(read());
  store[`${kind}:${id}`] = { remindAt, showAt: null };
  write(store);
}

export function rememberSnoozed(
  kind: 'task' | 'event',
  id: string,
  remindAt: string,
  showAt: number,
): void {
  const store = prune(read());
  store[`${kind}:${id}`] = { remindAt, showAt };
  write(store);
}
